import { useState } from 'react';
import { Sparkles, Copy, Check, Zap, Palette, GraduationCap, Code2, Coffee, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import GlowCard from '@/components/GlowCard';
import { cn } from '@/lib/utils';
import { useToolUsage } from '@/hooks/useToolUsage';
import { toast } from 'sonner';
import { callTAMAI } from '@/lib/tamAI';

interface Mood {
  id: string;
  label: string;
  icon: any;
  color: string;
  description: string;
  systemPrompt: string;
}

const moods: Mood[] = [
  {
    id: 'professional',
    label: 'Professional',
    icon: Zap,
    color: 'text-blue-500',
    description: 'Formal and business-appropriate',
    systemPrompt: 'Generate a professional, formal, and business-appropriate prompt. Use clear, concise language suitable for corporate environments.'
  },
  {
    id: 'creative',
    label: 'Creative',
    icon: Palette,
    color: 'text-purple-500',
    description: 'Imaginative and artistic',
    systemPrompt: 'Generate a creative, imaginative, and artistic prompt. Use vivid language, metaphors, and encourage out-of-the-box thinking.'
  },
  {
    id: 'academic',
    label: 'Academic',
    icon: GraduationCap,
    color: 'text-green-500',
    description: 'Scholarly and research-focused',
    systemPrompt: 'Generate an academic, scholarly, and research-focused prompt. Use precise terminology and encourage critical analysis.'
  },
  {
    id: 'technical',
    label: 'Technical',
    icon: Code2,
    color: 'text-orange-500',
    description: 'Developer and engineering focused',
    systemPrompt: 'Generate a technical, developer-focused prompt. Use programming terminology and encourage detailed technical specifications.'
  },
  {
    id: 'casual',
    label: 'Casual',
    icon: Coffee,
    color: 'text-pink-500',
    description: 'Friendly and conversational',
    systemPrompt: 'Generate a casual, friendly, and conversational prompt. Use relaxed language and a warm, approachable tone.'
  }
];

const PromptGenerator = () => {
  useToolUsage('prompt_generator'); // Track tool usage

  const [input, setInput] = useState('');
  const [selectedMood, setSelectedMood] = useState<Mood | null>(null);
  const [generatedPrompt, setGeneratedPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [displayedText, setDisplayedText] = useState('');

  const handleGenerate = async () => {
    if (!input.trim()) {
      toast.error('Please enter your idea or topic');
      return;
    }
    if (!selectedMood) {
      toast.error('Please select a mood/style');
      return;
    }

    setIsGenerating(true);
    setGeneratedPrompt('');
    setDisplayedText('');

    try {
      const fullPrompt = `You are an expert prompt engineer. ${selectedMood.systemPrompt}

Your task is to take the user's idea and transform it into an optimized, detailed prompt that will get the best results from AI models.

Rules:
- Output ONLY the improved prompt, nothing else
- Make it specific and detailed
- Include context and constraints where helpful
- Keep the tone matching the selected style
- Make it actionable and clear

Transform this into an optimized AI prompt:

${input}`;

      const resultText = await callTAMAI(fullPrompt, { temperature: 0.8, maxTokens: 1024 });

      setGeneratedPrompt(resultText);

      // Typing animation
      let i = 0;
      const typeInterval = setInterval(() => {
        if (i < resultText.length) {
          setDisplayedText(resultText.substring(0, i + 1));
          i++;
        } else {
          clearInterval(typeInterval);
        }
      }, 15);

      toast.success('Prompt generated successfully!');
    } catch (error) {
      console.error('Generation error:', error);
      toast.error('Failed to generate prompt. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(generatedPrompt);
    setCopied(true);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleRegenerate = () => {
    if (input && selectedMood) {
      handleGenerate();
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-3 rounded-xl bg-primary/10 pulse-glow">
          <Sparkles className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold">AI Prompt Generator</h1>
          <p className="text-muted-foreground">Transform your ideas into optimized AI prompts</p>
        </div>
        <Badge className="ml-auto bg-gradient-to-r from-purple-500 to-pink-500 text-white border-0">
          Powered by TAM API
        </Badge>
      </div>

      {/* Input Section */}
      <GlowCard className="card-lift">
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium mb-2 block">Your Idea or Topic</label>
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Enter your idea, question, or topic... e.g., 'Write a blog post about sustainable living'"
              className="min-h-[120px] resize-none"
            />
          </div>

          {/* Mood Selection */}
          <div>
            <label className="text-sm font-medium mb-3 block">Select Style/Mood</label>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 stagger-fade-in">
              {moods.map((mood) => {
                const Icon = mood.icon;
                const isSelected = selectedMood?.id === mood.id;

                return (
                  <button
                    key={mood.id}
                    onClick={() => setSelectedMood(mood)}
                    className={cn(
                      "p-4 rounded-xl border-2 transition-all duration-300 text-left group hover-bounce",
                      isSelected
                        ? "border-primary bg-primary/10 shadow-lg shadow-primary/20"
                        : "border-border/50 hover:border-primary/50 hover:bg-accent/50"
                    )}
                  >
                    <Icon className={cn(
                      "h-6 w-6 mb-2 transition-all duration-300",
                      mood.color,
                      isSelected ? "scale-110" : "group-hover:scale-110"
                    )} />
                    <p className="font-medium text-sm">{mood.label}</p>
                    <p className="text-xs text-muted-foreground mt-1">{mood.description}</p>
                  </button>
                );
              })}
            </div>
          </div>

          <Button
            onClick={handleGenerate}
            disabled={isGenerating || !input.trim() || !selectedMood}
            className="w-full h-12 text-lg font-semibold hover-bounce ripple"
          >
            {isGenerating ? (
              <>
                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Sparkles className="h-5 w-5 mr-2" />
                Generate Prompt
              </>
            )}
          </Button>
        </div>
      </GlowCard>

      {/* Result Section */}
      {(generatedPrompt || isGenerating) && (
        <GlowCard className="card-lift animate-slide-up">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              <h2 className="font-semibold">Generated Prompt</h2>
              {selectedMood && (
                <Badge variant="outline" className={selectedMood.color}>
                  {selectedMood.label}
                </Badge>
              )}
            </div>
            {generatedPrompt && !isGenerating && (
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRegenerate}
                  className="hover-bounce"
                >
                  <RefreshCw className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCopy}
                  className={cn(
                    "hover-bounce transition-all duration-300",
                    copied && "bg-success/20 border-success text-success"
                  )}
                >
                  {copied ? (
                    <Check className="h-4 w-4 success-pop" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
            )}
          </div>

          <div className="min-h-[150px] p-4 rounded-lg bg-muted/50 border border-border/50">
            {isGenerating ? (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Crafting your perfect prompt</span>
                <span className="typing-cursor" />
              </div>
            ) : (
              <p className="whitespace-pre-wrap">
                {displayedText}
                {displayedText.length < generatedPrompt.length && (
                  <span className="typing-cursor" />
                )}
              </p>
            )}
          </div>
        </GlowCard>
      )}

      {/* Tips */}
      <GlowCard className="card-lift">
        <h3 className="font-semibold mb-3 flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-primary" />
          Tips for Better Results
        </h3>
        <ul className="space-y-2 text-sm text-muted-foreground">
          <li className="flex items-start gap-2">
            <span className="text-primary">•</span>
            Be specific about your goal or desired outcome
          </li>
          <li className="flex items-start gap-2">
            <span className="text-primary">•</span>
            Include context about your target audience
          </li>
          <li className="flex items-start gap-2">
            <span className="text-primary">•</span>
            Mention any constraints or requirements
          </li>
          <li className="flex items-start gap-2">
            <span className="text-primary">•</span>
            Try different moods to see various prompt styles
          </li>
        </ul>
      </GlowCard>
    </div>
  );
};

export default PromptGenerator;
